import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ExternalLink,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { PageTitle, Panel } from "@/components/common";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "In attesa",
  approved: "Approvato",
  rejected: "Rifiutato",
};

const STATUS_CLASSES = {
  pending: "text-yellow-300 border-yellow-500/40 bg-yellow-500/10",
  approved: "text-green-300 border-green-500/40 bg-green-500/10",
  rejected: "text-red-300 border-red-500/40 bg-red-500/10",
};

const modeLabel = (mode) =>
  mode === "resurgence" ? "Resurgence" : "Battle Royale";

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("it-IT");
};

const formatKD = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const number = Number(value);

  if (Number.isNaN(number)) return "—";

  return number.toFixed(2);
};

export default function VerificheKD() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");

  const load = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/kd-verifications");
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(
        formatError(error.response?.data?.detail)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        statusFilter !== "all" &&
        row.status !== statusFilter
      ) {
        return false;
      }

      if (
        modeFilter !== "all" &&
        row.kd_mode !== modeFilter
      ) {
        return false;
      }

      if (!needle) return true;

      return [
        row.tournament_name,
        row.team_name,
        row.team_tag,
        row.activision_id,
        row.discord_username,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(needle)
        );
    });
  }, [rows, search, statusFilter, modeFilter]);

  const updateCheck = async (row, status) => {
    const key = `${row.team_id}:${row.player_index}:${status}`;
    setBusy(key);

    try {
      await api.put(
        `/teams/${row.team_id}/kd-check`,
        {
          index: row.player_index,
          status,
        }
      );

      toast.success(
        status === "approved"
          ? "Verifica K/D approvata"
          : "Verifica K/D rifiutata"
      );

      await load();
    } catch (error) {
      toast.error(
        formatError(error.response?.data?.detail)
      );
    } finally {
      setBusy(null);
    }
  };

  const correctKD = async (row) => {
    const current =
      row.verified_kd ??
      row.declared_kd ??
      "";

    const input = window.prompt(
      `Nuovo K/D ${modeLabel(row.kd_mode)} per ${row.activision_id}:`,
      current
    );

    if (input === null) return;

    const value = Number(
      String(input).replace(",", ".")
    );

    if (!Number.isFinite(value) || value < 0) {
      toast.error("Inserisci un K/D valido");
      return;
    }

    const key = `${row.player_id}:correct`;
    setBusy(key);

    try {
      await api.put(
        `/players/${row.player_id}/verified-kd`,
        {
          kd_mode: row.kd_mode,
          kd: value,
        }
      );

      toast.success("K/D corretto e certificato");
      await load();
    } catch (error) {
      toast.error(
        formatError(error.response?.data?.detail)
      );
    } finally {
      setBusy(null);
    }
  };

  const invalidateKD = async (row) => {
    if (!row.verified_kd && row.verified_kd !== 0) {
      toast.error("Nessuna verifica persistente da invalidare");
      return;
    }

    const confirmed = window.confirm(
      `Invalidare il K/D verificato ${modeLabel(row.kd_mode)} di ${row.activision_id}?`
    );

    if (!confirmed) return;

    const key = `${row.player_id}:invalidate`;
    setBusy(key);

    try {
      await api.delete(
        `/players/${row.player_id}/verified-kd`,
        {
          data: {
            kd_mode: row.kd_mode,
          },
        }
      );

      toast.success("Verifica K/D invalidata");
      await load();
    } catch (error) {
      toast.error(
        formatError(error.response?.data?.detail)
      );
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(
    () => ({
      total: rows.length,
      pending: rows.filter(
        (row) => row.status === "pending"
      ).length,
      approved: rows.filter(
        (row) => row.status === "approved"
      ).length,
      rejected: rows.filter(
        (row) => row.status === "rejected"
      ).length,
    }),
    [rows]
  );

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageTitle
          title="Verifiche K/D"
          subtitle="Gestione amministrativa delle verifiche K/D"
        />

        <button
          onClick={load}
          disabled={loading}
          className="btn-outline-gold clip-beveled px-4 py-2 flex items-center gap-2"
        >
          <RefreshCw
            size={16}
            className={loading ? "animate-spin" : ""}
          />
          Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Totali", counts.total],
          ["In attesa", counts.pending],
          ["Approvate", counts.approved],
          ["Rifiutate", counts.rejected],
        ].map(([label, value]) => (
          <Panel key={label} className="pt-5 pb-4">
            <div className="text-[10px] leading-4 uppercase tracking-widest text-[var(--silver)]">
              {label}
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {value}
            </div>
          </Panel>
        ))}
      </div>

      <Panel>
        <div className="grid md:grid-cols-[1fr_auto_auto] gap-3">
          <label className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--silver)]"
            />
            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Cerca torneo, squadra, giocatore..."
              className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-9 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
            />
          </label>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            className="bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
          >
            <option value="all">Tutti gli stati</option>
            <option value="pending">In attesa</option>
            <option value="approved">Approvati</option>
            <option value="rejected">Rifiutati</option>
          </select>

          <select
            value={modeFilter}
            onChange={(event) =>
              setModeFilter(event.target.value)
            }
            className="bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
          >
            <option value="all">Tutte le modalità</option>
            <option value="battle_royale">
              Battle Royale
            </option>
            <option value="resurgence">
              Resurgence
            </option>
          </select>
        </div>
      </Panel>

      <Panel>
        {loading ? (
          <div className="py-12 text-center text-[var(--silver)]">
            Caricamento verifiche K/D...
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="py-12 text-center text-[var(--silver)]">
            Nessuna verifica K/D trovata.
          </div>
        ) : (
          <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[1120px] text-sm">
              <thead>
                <tr className="border-b border-[var(--steel)]/40 text-left text-[10px] uppercase tracking-widest text-[var(--silver)]">
                  <th className="p-3">Torneo / Squadra</th>
                  <th className="p-3">Giocatore</th>
                  <th className="p-3">Modalità</th>
                  <th className="p-3">K/D</th>
                  <th className="p-3">Stato</th>
                  <th className="p-3">Prova</th>
                  <th className="p-3">Verifica</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => {
                  const checkKey =
                    `${row.team_id}:${row.player_index}`;

                  return (
                    <tr
                      key={`${row.team_id}:${row.player_id}:${row.kd_mode}`}
                      className="border-b border-[var(--steel)]/20 align-top"
                    >
                      <td className="p-3 min-w-[180px]">
                        <div className="text-white font-semibold">
                          {row.tournament_name || "—"}
                        </div>
                        <div className="text-xs text-[var(--silver)] mt-1">
                          {row.team_tag
                            ? `[${row.team_tag}] `
                            : ""}
                          {row.team_name || "—"}
                        </div>
                      </td>

                      <td className="p-3 min-w-[190px]">
                        <div className="text-white">
                          {row.activision_id || "—"}
                        </div>
                        <div className="text-xs text-[var(--silver)] mt-1">
                          {row.discord_username || "Discord non associato"}
                        </div>
                      </td>

                      <td className="p-3 whitespace-nowrap">
                        {modeLabel(row.kd_mode)}
                      </td>

                      <td className="p-3 whitespace-nowrap">
                        <div>
                          Dichiarato:{" "}
                          <strong className="text-white">
                            {formatKD(row.declared_kd)}
                          </strong>
                        </div>
                        <div className="text-xs mt-1">
                          Verificato:{" "}
                          <strong
                            className={
                              row.verified_valid
                                ? "text-green-300"
                                : "text-[var(--silver)]"
                            }
                          >
                            {formatKD(row.verified_kd)}
                          </strong>
                        </div>
                      </td>

                      <td className="p-3">
                        <span
                          className={`inline-block border px-2 py-1 text-xs ${
                            STATUS_CLASSES[row.status] ||
                            STATUS_CLASSES.pending
                          }`}
                        >
                          {STATUS_LABELS[row.status] || row.status}
                        </span>
                      </td>

                      <td className="p-3">
                        {row.screenshot_url ? (
                          <a
                            href={row.screenshot_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#67e8f9] hover:underline inline-flex items-center gap-1"
                          >
                            Screenshot
                            <ExternalLink size={13} />
                          </a>
                        ) : (
                          <span className="text-[var(--silver)]">
                            —
                          </span>
                        )}
                      </td>

                      <td className="p-3 min-w-[170px] text-xs">
                        <div>
                          {row.verification_source || "—"}
                        </div>
                        <div className="text-[var(--silver)] mt-1">
                          {formatDate(row.verified_at)}
                        </div>
                        {row.expires_at && (
                          <div className="text-[var(--silver)] mt-1">
                            Scade: {formatDate(row.expires_at)}
                          </div>
                        )}
                      </td>

                      <td className="p-3">
                        <div className="flex flex-wrap justify-end content-start gap-2 min-w-[210px]">
                          <button
                            onClick={() =>
                              updateCheck(row, "approved")
                            }
                            disabled={
                              busy === `${checkKey}:approved`
                            }
                            className="btn-outline-gold px-2 py-1 text-xs inline-flex items-center gap-1"
                            title="Approva"
                          >
                            <Check size={13} />
                            Approva
                          </button>

                          <button
                            onClick={() =>
                              updateCheck(row, "rejected")
                            }
                            disabled={
                              busy === `${checkKey}:rejected`
                            }
                            className="btn-outline-gold px-2 py-1 text-xs inline-flex items-center gap-1"
                            title="Rifiuta"
                          >
                            <X size={13} />
                            Rifiuta
                          </button>

                          <button
                            onClick={() => correctKD(row)}
                            disabled={
                              busy === `${row.player_id}:correct`
                            }
                            className="btn-outline-gold px-2 py-1 text-xs inline-flex items-center gap-1"
                            title="Correggi e certifica"
                          >
                            <ShieldCheck size={13} />
                            Correggi
                          </button>

                          <button
                            onClick={() => invalidateKD(row)}
                            disabled={
                              busy === `${row.player_id}:invalidate`
                            }
                            className="btn-outline-gold px-2 py-1 text-xs inline-flex items-center gap-1"
                            title="Invalida verifica persistente"
                          >
                            <Trash2 size={13} />
                            Invalida
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
